'use client';

import { useState, useEffect } from 'react';
import { Logger } from '@shared/logger';
import type { FormFieldMapping } from '../types/FormFieldMapping';
import type { RelatedDataConfig } from '../types/RelatedDataConfig';

/**
 * @group Types
 * 
 * @summary
 * <blockquote>Type definition for related data state that maps keys to arrays of related entities.</blockquote>
 * 
 * @example
 * ```typescript
 * const relatedData: RelatedDataState = {
 *   'users': userList,
 *   'roles': roleList
 * };
 * ```
 */
interface RelatedDataState {
  [key: string]: any[];
}

/**
 * @group Types
 * 
 * @summary
 * <blockquote>Return type for the useRelatedData hook containing related data, loading state, and error information.</blockquote>
 * 
 * @example
 * ```typescript
 * const result: UseRelatedDataResult = {
 *   relatedData: { 'users': userList },
 *   isLoading: false,
 *   error: null
 * };
 * ```
 */
interface UseRelatedDataResult {
  relatedData: RelatedDataState;
  isLoading: boolean;
  error: string | null;
}

export function useRelatedData(mappings: Record<string, FormFieldMapping>): UseRelatedDataResult {
  const [relatedData, setRelatedData] = useState<RelatedDataState>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const logger = new Logger();

  useEffect(() => {
    // Collect all related data configs from all mappings
    const allRelatedDataConfigs: RelatedDataConfig[] = [];
    
    Object.values(mappings).forEach(mapping => {
      if (mapping.relatedData) {
        allRelatedDataConfigs.push(...mapping.relatedData);
      }
    });

    // Remove duplicates based on key
    const uniqueConfigs = allRelatedDataConfigs.filter(
      (config, index, array) => array.findIndex(c => c.key === config.key) === index
    );

    if (uniqueConfigs.length === 0) {
      return;
    }

    setIsLoading(true);
    setError(null);

    // Fetch all related data concurrently
    const fetchPromises = uniqueConfigs.map(async (config) => {
      try {
        const response = await fetch(config.endpoint);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${config.key} from ${config.endpoint}`);
        }
        const data = await response.json();
        const transformedData = config.transform ? config.transform(data) : data;
        return { key: config.key, data: transformedData };
      } catch (err) {
        logger.error(`Error fetching ${config.key}`, 'data.users', undefined, err as Error);
        return { key: config.key, data: [], error: err };
      }
    });

    Promise.all(fetchPromises)
      .then((results) => {
        const newRelatedData: RelatedDataState = {};
        let hasError = false;

        results.forEach(({ key, data, error: fetchError }) => {
          if (fetchError) {
            hasError = true;
          } else {
            newRelatedData[key] = data;
          }
        });
        setRelatedData(newRelatedData);
        if (hasError) {
          setError('Some related data failed to load');
        }
      })
      .catch((err) => {
        logger.error('Error fetching related data', 'data.users', undefined, err as Error);
        setError('Failed to load related data');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [mappings]);

  return {
    relatedData,
    isLoading,
    error
  };
} 